import React, { useState, useEffect, useRef } from 'react';
import { Sun, Battery, BatteryCharging, Home, Euro, Phone, Mail, MapPin, ChevronRight, ChevronLeft, Zap, Leaf, Calculator, Check, User, Award, Shield, Monitor, Calendar, TrendingUp, X, Info, Cpu, Smartphone, Clock, Star, ArrowDown, ArrowUp, Sparkles, Cloud, Loader, HelpCircle, AlertCircle, CheckCircle, XCircle } from 'lucide-react';

const SolarSimulator = () => {
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [pvgisLoading, setPvgisLoading] = useState(false);
  const [pvgisStep, setPvgisStep] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [addressSuggestions, setAddressSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const [showModal, setShowModal] = useState(null);
  const [showTooltip, setShowTooltip] = useState(null);
  const [showBillModal, setShowBillModal] = useState(false);
  const [viewMode, setViewMode] = useState('monthly');
  
  // Refs pour gérer le focus et les clics en dehors
  const searchInputRef = useRef(null);
  const suggestionsRef = useRef(null);
  const searchContainerRef = useRef(null);
  
  const [formData, setFormData] = useState({
    address: '',
    coordinates: null,
    panelType: 'standard',
    panelCount: 12,
    batteryType: 'none',
    batteryCapacity: 10,
    withCarCharger: false,
    leasingDuration: 12,
    monthlyBill: 200,
    electricityPrice: 0.2276,
    email: '',
    phone: '',
    name: '',
    lastName: '',
    acceptMarketing: false
  });
  
  const [solarProduction, setSolarProduction] = useState(null);
  const [monthlyPayment, setMonthlyPayment] = useState(null);
  const [savings, setSavings] = useState(null);
  const [tracking, setTracking] = useState({});

  // Scroll to top when step changes
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [step]);

  // Gestion des clics en dehors des suggestions
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchContainerRef.current && !searchContainerRef.current.contains(event.target)) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Logo click handler
  const handleLogoClick = () => {
    window.open('https://www.acfluide.fr', '_blank', 'noopener,noreferrer');
  };

  // Phone call handler
  const handlePhoneCall = () => {
    window.location.href = 'tel:+33465844645';
  };

  // Email handler
  const handleEmailClick = () => {
    window.location.href = 'mailto:contact@acfluide.fr';
  };

  // Tracking extraction
  const extractTrackingParams = () => {
    const urlParams = new URLSearchParams(window.location.search);
    const referrer = document.referrer || '';
    const landingPage = window.location.href;
    
    const trackingData = {
      utm_source: urlParams.get('utm_source') || detectSourceFromReferrer(referrer),
      utm_medium: urlParams.get('utm_medium') || detectMediumFromReferrer(referrer),
      utm_campaign: urlParams.get('utm_campaign') || '',
      utm_content: urlParams.get('utm_content') || '',
      utm_term: urlParams.get('utm_term') || '',
      gclid: urlParams.get('gclid') || '',
      fbclid: urlParams.get('fbclid') || '',
      referrer: referrer,
      landing_page: landingPage
    };

    Object.keys(trackingData).forEach(key => {
      if (!trackingData[key]) {
        delete trackingData[key];
      }
    });

    console.log('Paramètres de tracking détectés:', trackingData);
    return trackingData;
  };

  const detectSourceFromReferrer = (referrer) => {
    if (!referrer) return 'direct';
    
    const domain = referrer.toLowerCase();
    
    if (domain.includes('google.')) return 'google';
    if (domain.includes('facebook.') || domain.includes('fb.')) return 'facebook';
    if (domain.includes('instagram.')) return 'instagram';
    if (domain.includes('tiktok.')) return 'tiktok';
    if (domain.includes('linkedin.')) return 'linkedin';
    if (domain.includes('youtube.')) return 'youtube';
    if (domain.includes('twitter.') || domain.includes('t.co')) return 'twitter';
    if (domain.includes('bing.')) return 'bing';
    
    return 'referral';
  };

  const detectMediumFromReferrer = (referrer) => {
    if (!referrer) return 'direct';
    
    const domain = referrer.toLowerCase();
    
    if (domain.includes('google.') || domain.includes('bing.')) return 'organic';
    if (domain.includes('facebook.') || domain.includes('instagram.') || 
        domain.includes('tiktok.') || domain.includes('linkedin.') || 
        domain.includes('youtube.') || domain.includes('twitter.')) return 'social';
    
    return 'referral';
  };

  useEffect(() => {
    const trackingParams = extractTrackingParams();
    setTracking(trackingParams);
  }, []);

  // Analytics setup
  useEffect(() => {
    const gtagScript = document.createElement('script');
    gtagScript.async = true;
    gtagScript.src = 'https://www.googletagmanager.com/gtag/js?id=G-BFF8JEQZDV';
    document.head.appendChild(gtagScript);

    const gtagConfigScript = document.createElement('script');
    gtagConfigScript.innerHTML = `
      window.dataLayer = window.dataLayer || [];
      function gtag(){dataLayer.push(arguments);}
      gtag('js', new Date());
      gtag('config', 'G-BFF8JEQZDV');
    `;
    document.head.appendChild(gtagConfigScript);

    const clarityScript = document.createElement('script');
    clarityScript.type = 'text/javascript';
    clarityScript.innerHTML = `
      (function(c,l,a,r,i,t,y){
        c[a]=c[a]||function(){(c[a].q=c[a].q||[]).push(arguments)};
        t=l.createElement(r);t.async=1;t.src="https://www.clarity.ms/tag/"+i;
        y=l.getElementsByTagName(r)[0];y.parentNode.insertBefore(t,y);
      })(window, document, "clarity", "script", "rq1zul25yq");
    `;
    document.head.appendChild(clarityScript);
    
    return () => {
      if (document.head.contains(gtagScript)) {
        document.head.removeChild(gtagScript);
      }
      if (document.head.contains(gtagConfigScript)) {
        document.head.removeChild(gtagConfigScript);
      }
      if (document.head.contains(clarityScript)) {
        document.head.removeChild(clarityScript);
      }
    };
  }, []);

  // Tooltips data
  const tooltips = {
    pvgis: {
      title: "Calcul PVGIS",
      content: "PVGIS est l'outil de référence européen pour calculer la production solaire. Il analyse l'exposition, l'inclinaison et l'orientation de votre toit pour une estimation précise."
    },
    autoconsommation: {
      title: "Autoconsommation",
      content: "Pourcentage de votre production solaire que vous consommez directement. Plus ce taux est élevé, plus vous économisez sur votre facture électrique."
    },
    vente_surplus: {
      title: "Vente du surplus",
      content: "L'électricité que vous ne consommez pas est automatiquement revendue à EDF OA au tarif de 0,04€/kWh, vous générant un revenu complémentaire."
    },
    kwc: {
      title: "Puissance en kWc",
      content: "Le kilowatt-crête (kWc) mesure la puissance maximale de vos panneaux solaires dans des conditions optimales d'ensoleillement."
    },
    loa: {
      title: "Location avec Option d'Achat",
      content: "Formule sans apport initial. AC Fluide reste propriétaire et assure la maintenance. Vous pouvez racheter à partir de la 10e année."
    },
    garantie: {
      title: "Garantie AC Fluide",
      content: "Garantie pièces et main d'œuvre 20 ans en location + garantie constructeur. Maintenance et suivi inclus pendant toute la durée."
    },
    rge: {
      title: "Certification RGE",
      content: "Reconnu Garant de l'Environnement - Certification obligatoire pour les aides publiques et gage de qualité professionnelle."
    },
    economies_25_ans: {
      title: "Économies sur 25 ans",
      content: "Calcul tenant compte de l'inflation du prix de l'électricité (+3% par an) et de la durée de vie des panneaux (25 ans minimum)."
    },
    batterie_virtuelle: {
      title: "Batterie Virtuelle - Notre Recommandation",
      content: "La solution la plus avancée ! Stockage intelligent dans le cloud avec optimisation automatique. Pas d'usure, pas de maintenance, performance constante."
    },
    batterie_physique: {
      title: "Batterie Physique",
      content: "Stockage à domicile pour une autonomie maximale. Nécessite un investissement plus important et une maintenance régulière."
    },
    sans_batterie: {
      title: "Sans Batterie",
      content: "Solution économique de base. Le surplus est revendu au réseau électrique à 0,04€/kWh."
    }
  };

  // Panel types configuration
  const panelTypes = {
    standard: { 
      name: 'Standard', 
      basePrice: 2100,
      description: 'Excellent rapport qualité-prix pour débuter dans le solaire',
      efficiency: 0.20,
      efficiencyLabel: '20%',
      warranty: '20 ans',
      pvgisLoss: 12,
      badge: '💰',
      recommendation: 'Économique'
    },
    performance: { 
      name: 'Performance', 
      basePrice: 2200,
      description: 'Technologie premium pour maximiser vos économies',
      efficiency: 0.22,
      efficiencyLabel: '22%',
      warranty: '25 ans',
      pvgisLoss: 9,
      badge: '⭐',
      recommendation: 'Recommandé'
    },
    francaise: { 
      name: 'Made in France', 
      basePrice: 2300,
      description: 'Fabrication française, soutenez l\'économie locale',
      efficiency: 0.21,
      efficiencyLabel: '21%',
      warranty: '25 ans',
      pvgisLoss: 10,
      badge: '🇫🇷',
      recommendation: 'Impact local'
    }
  };

  // Simplified leasing options without rates
  const leasingOptions = {
    10: { years: 10, popular: false },
    12: { years: 12, popular: false },
    15: { years: 15, popular: true },
    20: { years: 20, popular: false }
  };

  const batteryTypes = {
    none: {
      name: 'Sans batterie',
      description: 'Solution simple et économique',
      detailedDescription: 'Votre surplus de production est automatiquement revendu à EDF à 0,13€/kWh.',
      autoconsumption: 55,
      cost: 0,
      icon: Sun,
      advantages: [
        '0€ d\'investissement supplémentaire',
        'Maintenance nulle', 
        'Revente garantie'
      ],
      disadvantages: [
        'Dépendance au réseau',
        'Pas d\'autonomie'
      ]
    },
    physical: {
      name: 'Batterie physique',
      description: 'Stockage à domicile pour une autonomie maximale',
      detailedDescription: 'Stockage physique chez vous avec une capacité de 5 à 20 kWh selon vos besoins.',
      autoconsumption: 85,
      costPerKwh: 800,
      icon: Battery,
      advantages: [
        'Autonomie maximale',
        'Stockage direct chez vous',
        'Sécurité énergétique'
      ],
      disadvantages: [
        'Investissement plus élevé',
        'Maintenance nécessaire',
        'Usure avec le temps'
      ]
    },
    virtual: {
      name: 'Batterie virtuelle',
      description: 'Stockage cloud intelligent - RECOMMANDÉ',
      detailedDescription: 'Stockage intelligent dans le cloud avec optimisation automatique de votre consommation.',
      autoconsumption: 95,
      costPerKwc: 1,
      icon: Cloud,
      advantages: [
        'Optimisation automatique 24h/24',
        'Aucune usure ni maintenance',
        'Performance constante',
        'Gestion intelligente'
      ],
      disadvantages: [
        'Coût mensuel fixe',
        'Dépendant de la technologie'
      ],
      isRecommended: true
    }
  };

  const services = {
    installation: {
      name: 'Installation complète',
      description: 'Équipe certifiée RGE, pose professionnelle en 1-2 jours, raccordement au réseau électrique, mise en service et formation à l\'utilisation.',
      included: true,
      icon: User,
      details: "Une équipe de techniciens certifiés RGE réalise votre installation solaire chez vous, à la date convenue en amont. Il faut généralement un à deux jours pour poser les panneaux solaires sur votre toiture et les brancher à votre tableau électrique. La réalisation de votre installation solaire est incluse dans votre offre."
    },
    garantie: {
      name: 'Garantie premium',
      description: 'Garantie produit 25 ans, garantie installation 20 ans, assurance décennale, maintenance préventive, hotline technique 7j/7.',
      included: true,
      icon: Award,
      details: "En choisissant AC Fluide pour votre projet solaire, vous bénéficiez de notre garantie pièces et main d'œuvre (en plus de la garantie constructeur) pendant 20 ans en location (10 pour la batterie) et 10 ans en achat comptant."
    },
    application: {
      name: 'Application de suivi',
      description: 'Monitoring en temps réel de votre production, suivi des économies, alertes maintenance, historique détaillé, comparaison avec les prévisions.',
      included: true,
      icon: Monitor
    },
    carCharger: {
      name: 'Borne de recharge 7kW',
      description: 'Station de recharge intelligente, compatible tous véhicules électriques, gestion optimisée avec vos panneaux, installation incluse.',
      included: false,
      price: 1200,
      icon: Zap
    }
  };

  // Address search - CORRIGÉ ET AMÉLIORÉ
  const searchAddress = async (query) => {
    console.log('🔍 Recherche adresse pour:', query);
    
    if (query.length < 3) {
      console.log('❌ Query trop courte, reset suggestions');
      setAddressSuggestions([]);
      setShowSuggestions(false);
      setIsSearching(false);
      return;
    }
    
    setIsSearching(true);
    console.log('⏳ Démarrage recherche API...');
    
    try {
      const response = await fetch(`https://api-adresse.data.gouv.fr/search/?q=${encodeURIComponent(query)}&limit=8`);
      
      if (!response.ok) {
        throw new Error(`Erreur HTTP: ${response.status}`);
      }
      
      const data = await response.json();
      console.log('✅ Résultats API adresse:', data);
      
      if (data.features && data.features.length > 0) {
        console.log('📋 Mise à jour suggestions:', data.features.length, 'résultats');
        setAddressSuggestions(data.features);
        setShowSuggestions(true);
      } else {
        console.log('🚫 Aucun résultat trouvé');
        setAddressSuggestions([]);
        setShowSuggestions(false);
      }
    } catch (error) {
      console.error('❌ Erreur recherche adresse:', error);
      setAddressSuggestions([]);
      setShowSuggestions(false);
    }
    
    setIsSearching(false);
    console.log('✅ Recherche terminée, isSearching:', false);
  };

  // Debounce pour éviter trop d'appels API
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (formData.address) {
        searchAddress(formData.address);
      }
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [formData.address]);

  // Consumption calculations
  const getEstimatedYearlyConsumption = () => {
    return (formData.monthlyBill * 12) / formData.electricityPrice;
  };

  const getAutoconsumptionRate = () => {
    if (!solarProduction) {
      return batteryTypes[formData.batteryType].autoconsumption;
    }

    const yearlyConsumption = getEstimatedYearlyConsumption();
    const maxPossibleRate = Math.min(solarProduction, yearlyConsumption) / yearlyConsumption;
    
    let autoconsumptionEfficiency;
    
    if (formData.batteryType === 'none') {
      if (maxPossibleRate <= 0.3) {
        autoconsumptionEfficiency = 0.65;
      } else if (maxPossibleRate <= 0.7) {
        autoconsumptionEfficiency = 0.60;
      } else if (maxPossibleRate <= 1.0) {
        autoconsumptionEfficiency = 0.55;
      } else {
        autoconsumptionEfficiency = Math.max(0.45, 0.9 / (solarProduction / yearlyConsumption));
      }
    } else if (formData.batteryType === 'virtual') {
      autoconsumptionEfficiency = 0.98;
    } else if (formData.batteryType === 'physical') {
      const dailyProduction = solarProduction / 365;
      const batteryCapacity = formData.batteryCapacity;
      
      if (batteryCapacity >= dailyProduction * 2) {
        autoconsumptionEfficiency = 0.95;
      } else if (batteryCapacity >= dailyProduction) {
        autoconsumptionEfficiency = 0.85;
      } else {
        autoconsumptionEfficiency = 0.65 + (batteryCapacity / dailyProduction) * 0.2;
      }
    }
    
    const productionUsed = Math.min(solarProduction * autoconsumptionEfficiency, yearlyConsumption);
    const finalRate = (productionUsed / yearlyConsumption) * 100;
    
    return Math.round(finalRate);
  };

  // Simplified price calculation - just base monthly payment
  const getEstimatedMonthlyPayment = (customDuration = null) => {
    const duration = customDuration || formData.leasingDuration;
    const panelType = panelTypes[formData.panelType];
    const baseMonthlyPrice = Math.round((formData.panelCount * panelType.basePrice) / (duration * 12));
    
    let monthlyPayment = baseMonthlyPrice;
    
    // Add battery cost if physical
    if (formData.batteryType === 'physical') {
      const batteryMonthly = Math.round((formData.batteryCapacity * batteryTypes.physical.costPerKwh) / (duration * 12));
      monthlyPayment += batteryMonthly;
    }
    
    // Add car charger cost
    if (formData.withCarCharger) {
      const chargerMonthly = Math.round(services.carCharger.price / (duration * 12));
      monthlyPayment += chargerMonthly;
    }
    
    // Add virtual battery monthly cost
    if (formData.batteryType === 'virtual') {
      const kwc = formData.panelCount * 0.5;
      monthlyPayment += kwc * batteryTypes.virtual.costPerKwc;
    }
    
    return monthlyPayment;
  };

  const calculateNewBill = () => {
    if (!solarProduction) {
      return formData.monthlyBill;
    }

    const estimatedYearlyConsumption = getEstimatedYearlyConsumption();
    const autoconsumptionRate = getAutoconsumptionRate() / 100;
    
    const monthlyConsumption = estimatedYearlyConsumption / 12;
    const monthlyAutoconsumption = monthlyConsumption * autoconsumptionRate;
    const remainingConsumption = monthlyConsumption - monthlyAutoconsumption;
    
    let newBill = remainingConsumption * formData.electricityPrice;
    
    if (formData.batteryType === 'virtual') {
      const kwc = formData.panelCount * 0.5;
      const virtualBatteryCost = kwc * batteryTypes.virtual.costPerKwc;
      newBill += virtualBatteryCost;
    }
    
    return Math.round(Math.max(newBill, 0));
  };

  const calculateSavings = () => {
    if (!solarProduction) {
      return 0;
    }
    
    const newBill = calculateNewBill();
    const monthlyEconomies = Math.max(0, formData.monthlyBill - newBill);
    return Math.round(monthlyEconomies);
  };

  // PVGIS calculation with animated steps
  const calculateSolarProduction = async () => {
    if (!formData.coordinates) return;
    
    setPvgisLoading(true);
    setPvgisStep(0);
    
    const { lat, lon } = formData.coordinates;
    const kwc = formData.panelCount * 0.5;
    const panelType = panelTypes[formData.panelType];
    
    const efficiencyRatio = panelType.efficiency / 0.22;
    const adjustedKwc = kwc * efficiencyRatio;

    // Simulation des étapes avec délais réalistes
    const steps = [
      { name: 'Géolocalisation', delay: 300 },
      { name: 'Données météo', delay: 400 },
      { name: 'Calcul production', delay: 500 },
      { name: 'Optimisation', delay: 400 },
      { name: 'Économies', delay: 400 }
    ];

    for (let i = 0; i < steps.length; i++) {
      setPvgisStep(i);
      await new Promise(resolve => setTimeout(resolve, steps[i].delay));
    }
    
    try {
      console.log('Démarrage calcul PVGIS pour:', { lat, lon, kwc: adjustedKwc, type: formData.panelType });
      
      const pvgisPayload = {
        lat: lat,
        lon: lon,
        usehorizon: 1,
        raddatabase: "PVGIS-SARAH3",
        peakpower: parseFloat(adjustedKwc.toFixed(2)),
        pvtechchoice: "crystSi",
        mountingplace: "free",
        loss: panelType.pvgisLoss,
        angle: 35,
        aspect: 0,
        optimalinclination: 0,
        optimalangles: 0,
        inclined_axis: 0,
        vertical_axis: 0,
        twoaxis: 0,
        outputformat: "json",
        browser: 0,
        additional_data: {
          source_call: "pvgisHome",
          pvgis_customer_id: "",
          uid: "2025021911332208321798M7Wl",
          search_data: {
            steps: {
              country: "France",
              city: "",
              street: "",
              street_number: ""
            },
            address_bar: "France"
          },
          simulation_type: "grid_connected",
          has_same_payload: false
        }
      };

      const pvgisHeaders = {
        'accept': '*/*',
        'accept-language': 'fr-FR,fr;q=0.9,en-US;q=0.8,en;q=0.7,es;q=0.6',
        'content-type': 'application/json',
        'origin': 'https://pvgis.com',
        'referer': 'https://pvgis.com/',
        'sec-ch-ua': '"Google Chrome";v="137", "Chromium";v="137", "Not/A)Brand";v="24"',
        'sec-ch-ua-mobile': '?0',
        'sec-ch-ua-platform': '"Windows"',
        'sec-fetch-dest': 'empty',
        'sec-fetch-mode': 'cors',
        'sec-fetch-site': 'same-site',
        'user-agent': 'Mozilla/5.0 (Windows NT 10.0; Win64; x64) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/137.0.0.0 Safari/537.36'
      };
      
      try {
        console.log('Appel API PVGIS v5_3...');
        const response = await fetch('https://api.pvgis.com/api/v5_3/pvgis_grid_connected_or_tracking', {
          method: 'POST',
          headers: pvgisHeaders,
          body: JSON.stringify(pvgisPayload)
        });
        
        if (response.ok) {
          const data = await response.json();
          console.log('Données PVGIS reçues:', data);
          
          if (data && data.outputs && data.outputs.totals && data.outputs.totals.fixed) {
            const yearlyProduction = data.outputs.totals.fixed.E_y;
            console.log('PVGIS v5_3 réussi:', yearlyProduction, 'kWh/an');
            setSolarProduction(yearlyProduction);
            calculateFinancials(yearlyProduction);
            return;
          } else if (data && data.outputs && data.outputs.monthly) {
            const monthlyData = data.outputs.monthly;
            const yearlyProduction = monthlyData.reduce((sum, month) => sum + (month.E_m || 0), 0);
            console.log('PVGIS v5_3 (mensuel) réussi:', yearlyProduction, 'kWh/an');
            setSolarProduction(yearlyProduction);
            calculateFinancials(yearlyProduction);
            return;
          } else if (data && data.year_total) {
            const yearlyProduction = data.year_total;
            console.log('PVGIS v5_3 (total annuel) réussi:', yearlyProduction, 'kWh/an');
            setSolarProduction(yearlyProduction);
            calculateFinancials(yearlyProduction);
            return;
          } else {
            console.log('Structure de données PVGIS inattendue:', data);
            throw new Error('Structure de données non reconnue');
          }
        } else {
          console.log('Erreur HTTP PVGIS:', response.status, response.statusText);
          throw new Error(`Erreur HTTP: ${response.status}`);
        }
      } catch (error) {
        console.log('API PVGIS v5_3 échouée:', error.message);
        throw error;
      }
      
    } catch (error) {
      console.error('Erreur PVGIS, utilisation estimation avancée:', error);
      
      let baseProductionPerKwp = 1250;
      
      if (lat > 48.5) {
        baseProductionPerKwp = 1150;
      } else if (lat > 46.5) {
        baseProductionPerKwp = 1250;
      } else if (lat > 44) {
        baseProductionPerKwp = 1350;
      } else {
        baseProductionPerKwp = 1450;
      }
      
      if (lon < 0) {
        baseProductionPerKwp *= 0.95;
      } else if (lon > 5) {
        baseProductionPerKwp *= 1.02;
      }
      
      const adjustmentFactor = (100 - panelType.pvgisLoss) / 100;
      baseProductionPerKwp *= adjustmentFactor;
      
      console.log(`Position: ${lat.toFixed(2)}, ${lon.toFixed(2)} - Production estimée: ${baseProductionPerKwp} kWh/kWp/an`);
      
      const simulatedProduction = kwc * baseProductionPerKwp * efficiencyRatio;
      const finalProduction = Math.round(simulatedProduction);
      
      console.log('Production finale estimée:', finalProduction, 'kWh/an');
      setSolarProduction(finalProduction);
      calculateFinancials(finalProduction);
      
    } finally {
      setPvgisLoading(false);
      setPvgisStep(0);
    }
  };

  const calculateFinancials = (yearlyProduction) => {
    const monthlyPaymentCalc = getEstimatedMonthlyPayment();
    setMonthlyPayment(monthlyPaymentCalc);
    
    const estimatedYearlyConsumption = getEstimatedYearlyConsumption();
    const autoconsumptionRate = getAutoconsumptionRate() / 100;
    const sellbackPrice = 0.04;
    
    const monthlyConsumption = estimatedYearlyConsumption / 12;
    const monthlyAutoconsumption = monthlyConsumption * autoconsumptionRate;
    const remainingConsumption = monthlyConsumption - monthlyAutoconsumption;
    
    let calculatedNewBill = remainingConsumption * formData.electricityPrice;
    
    if (formData.batteryType === 'virtual') {
      const kwc = formData.panelCount * 0.5;
      const virtualBatteryCost = kwc * batteryTypes.virtual.costPerKwc;
      calculatedNewBill += virtualBatteryCost;
    }
    
    const monthlyEconomies = formData.monthlyBill - calculatedNewBill;
    
    const yearlyAutoconsumed = monthlyAutoconsumption * 12;
    const excessProduction = Math.max(0, yearlyProduction - yearlyAutoconsumed);
    
    let incomeFromSellback = 0;
    if (formData.batteryType === 'none') {
      incomeFromSellback = excessProduction * sellbackPrice;
    }
    
    const totalYearlySavings = (monthlyEconomies * 12) + incomeFromSellback;
    
    // Calculate savings over 25 years with inflation
    const savingsOver25Years = Array.from({length: 25}, (_, year) => {
      const inflationFactor = Math.pow(1.03, year);
      return totalYearlySavings * inflationFactor;
    }).reduce((sum, savings) => sum + savings, 0);
    
    setSavings({
      monthly: Math.round(monthlyEconomies),
      yearly: Math.round(totalYearlySavings),
      total: Math.round(savingsOver25Years),
      newMonthlyBill: Math.round(calculatedNewBill),
      totalMonthlySavings: Math.round(monthlyEconomies - monthlyPaymentCalc),
      autoconsumptionRate: Math.round(autoconsumptionRate * 100),
      autoconsumedKwh: Math.round(yearlyAutoconsumed),
      excessKwh: Math.round(excessProduction),
      estimatedConsumption: Math.round(estimatedYearlyConsumption),
      productionRatio: Math.round((yearlyProduction / estimatedYearlyConsumption) * 100)
    });

    console.log('Calculs financiers terminés:', {
      production: yearlyProduction,
      autoconsumption: Math.round(autoconsumptionRate * 100) + '%',
      monthlyEconomies: Math.round(monthlyEconomies),
      newBill: Math.round(calculatedNewBill)
    });
  };

  useEffect(() => {
    if (step === 2 && formData.coordinates) {
      setSolarProduction(null);
      setSavings(null);
      setMonthlyPayment(null);
      calculateSolarProduction();
    }
  }, [formData.panelType, formData.panelCount, formData.batteryType, formData.batteryCapacity, formData.monthlyBill, formData.electricityPrice, step]);

  const handleAddressSelect = (feature) => {
    console.log('Adresse sélectionnée:', feature.properties.label);
    
    setFormData({
      ...formData,
      address: feature.properties.label,
      coordinates: {
        lat: feature.geometry.coordinates[1],
        lon: feature.geometry.coordinates[0]
      }
    });
    
    setAddressSuggestions([]);
    setShowSuggestions(false);
    
    if (typeof gtag !== 'undefined') {
      gtag('event', 'address_selected', {
        event_category: 'simulator',
        event_label: 'address_validation',
        address_city: feature.properties.city || 'unknown',
        utm_source: tracking.utm_source || 'direct',
        utm_campaign: tracking.utm_campaign || 'none'
      });
    }
  };

  const handleAddressInputChange = (e) => {
    const value = e.target.value;
    console.log('📝 Changement input adresse:', value);
    
    setFormData({ ...formData, address: value });
    
    // Reset coordinates if address is cleared
    if (!value) {
      console.log('🧹 Reset adresse et coordonnées');
      setFormData({ ...formData, address: '', coordinates: null });
      setAddressSuggestions([]);
      setShowSuggestions(false);
    }
  };

  const nextStep = () => {
    if (step === 1 && (!formData.coordinates || !formData.monthlyBill || formData.monthlyBill < 50)) {
      alert('Veuillez sélectionner une adresse valide et indiquer votre facture mensuelle');
      return;
    }
    if (step === 2 && formData.coordinates && !solarProduction) {
      calculateSolarProduction();
    }
    if (step === 3 && (!formData.email || !formData.phone || !formData.name || !formData.lastName)) {
      alert('Veuillez remplir tous les champs');
      return;
    }
    if (step === 3) {
      handleSubmitAndProceed();
      return;
    }
    
    if (typeof gtag !== 'undefined') {
      gtag('event', 'step_completed', {
        event_category: 'simulator',
        event_label: `step_${step}`,
        step_number: step,
        utm_source: tracking.utm_source || 'direct',
        utm_campaign: tracking.utm_campaign || 'none'
      });
    }
    
    setStep(step + 1);
  };

  const prevStep = () => {
    setStep(step - 1);
  };

  const validateBeforeSubmit = () => {
    const errors = [];
    
    if (!formData.name || formData.name.length < 2) {
      errors.push('Prénom requis (minimum 2 caractères)');
    }
    
    if (!formData.lastName || formData.lastName.length < 2) {
      errors.push('Nom requis (minimum 2 caractères)');
    }
    
    if (!formData.email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      errors.push('Email valide requis');
    }
    
    if (!formData.phone || !/^(?:\+33|0)[1-9](?:[0-9]{8})$/.test(formData.phone.replace(/\s/g, ''))) {
      errors.push('Numéro de téléphone français requis');
    }
    
    if (!formData.address || formData.address.length < 5) {
      errors.push('Adresse complète requise');
    }
    
    if (!formData.coordinates) {
      errors.push('Coordonnées GPS manquantes');
    }
    
    if (!formData.acceptMarketing) {
      errors.push('Consentement requis pour être recontacté');
    }
    
    if (errors.length > 0) {
      alert(`Veuillez corriger les erreurs suivantes :\n\n${errors.map(e => `• ${e}`).join('\n')}`);
      return false;
    }
    
    return true;
  };

  const handleSubmitAndProceed = async () => {
    if (isSubmitting) return;
    
    if (!validateBeforeSubmit()) {
      return;
    }
    
    try {
      setIsSubmitting(true);
      
      const leadData = {
        name: formData.name,
        lastName: formData.lastName,
        email: formData.email,
        phone: formData.phone,
        address: formData.address,
        coordinates: formData.coordinates,
        panelType: formData.panelType,
        panelCount: formData.panelCount,
        batteryType: formData.batteryType,
        batteryCapacity: formData.batteryCapacity,
        withCarCharger: formData.withCarCharger,
        leasingDuration: formData.leasingDuration,
        monthlyBill: formData.monthlyBill,
        electricityPrice: formData.electricityPrice,
        solarProduction: solarProduction,
        monthlyPayment: monthlyPayment,
        savings: savings,
        acceptMarketing: formData.acceptMarketing,
        tracking: tracking
      };

      console.log('Envoi du lead vers l\'API...', {
        email: leadData.email,
        name: `${leadData.name} ${leadData.lastName}`,
        address: leadData.address,
        source: tracking.utm_source || 'direct',
        campaign: tracking.utm_campaign || 'none'
      });

      const response = await fetch('https://api-simulateur.acfluide.fr/api/leads', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify(leadData)
      });

      const result = await response.json();

      if (response.ok && result.success) {
        console.log('Lead enregistré avec succès:', result);
        
        setStep(4);
        
        if (typeof gtag !== 'undefined') {
          gtag('event', 'lead_submitted', {
            event_category: 'conversion',
            event_label: 'solar_quote_request',
            value: monthlyPayment || 0,
            currency: 'EUR',
            panel_count: formData.panelCount,
            panel_type: formData.panelType,
            battery_type: formData.batteryType,
            monthly_bill: formData.monthlyBill,
            utm_source: tracking.utm_source || 'direct',
            utm_medium: tracking.utm_medium || 'none',
            utm_campaign: tracking.utm_campaign || 'none',
            gclid: tracking.gclid || '',
            fbclid: tracking.fbclid || ''
          });
          
          gtag('event', 'conversion', {
            send_to: 'G-BFF8JEQZDV/conversion',
            value: monthlyPayment || 0,
            currency: 'EUR',
            utm_source: tracking.utm_source || 'direct'
          });
        }
        
        if (window.clarity) {
          window.clarity('event', 'lead_submitted', {
            source: tracking.utm_source || 'direct',
            campaign: tracking.utm_campaign || 'none'
          });
        }

      } else {
        console.error('Erreur serveur:', result);
        
        if (result.errors && result.errors.length > 0) {
          const errorMessages = result.errors.map(err => `• ${err.msg}`).join('\n');
          alert(`Erreur de validation :\n\n${errorMessages}\n\nVeuillez corriger ces informations.`);
        } else {
          alert(`Erreur lors de l'enregistrement : ${result.message || 'Erreur inconnue'}\n\nVeuillez réessayer ou contacter notre support.`);
        }
        return;
      }

    } catch (error) {
      console.error('Erreur réseau:', error);
      
      alert(`Problème de connexion réseau.

Votre simulation n'a pas pu être enregistrée automatiquement.

Solutions :
1. Vérifiez votre connexion internet
2. Réessayez dans quelques instants
3. Contactez notre support au +33 4 65 84 46 45

Vos données de simulation :
Email : ${formData.email}
Téléphone : ${formData.phone}
Nom : ${formData.name} ${formData.lastName}
${formData.panelCount} panneaux ${panelTypes[formData.panelType].name}
Économies estimées : ${calculateSavings()}€/mois`);
      
      return;
    } finally {
      setIsSubmitting(false);
    }
  };

  // Tooltip component
  const Tooltip = ({ id, children }) => {
    return (
      <div className="simulator-tooltip-wrapper">
        {children}
        <button 
          className="simulator-tooltip-trigger"
          onClick={() => setShowTooltip(showTooltip === id ? null : id)}
          type="button"
        >
          <HelpCircle size={16} />
        </button>
        {showTooltip === id && (
          <div className="simulator-tooltip-content">
            <div className="simulator-tooltip-header">
              <h4>{tooltips[id]?.title}</h4>
              <button 
                onClick={() => setShowTooltip(null)}
                className="simulator-tooltip-close"
                type="button"
              >
                <X size={14} />
              </button>
            </div>
            <p>{tooltips[id]?.content}</p>
          </div>
        )}
      </div>
    );
  };

  const Modal = ({ service, onClose }) => {
    if (!service) return null;
    
    return (
      <div className="simulator-modal-overlay" onClick={onClose}>
        <div className="simulator-modal-content" onClick={(e) => e.stopPropagation()}>
          <div className="simulator-modal-header">
            <div className="simulator-modal-icon">
              <service.icon />
            </div>
            <h3>{service.name}</h3>
            <button onClick={onClose} className="simulator-modal-close">
              <X size={20} />
            </button>
          </div>
          <div className="simulator-modal-body">
            <p>{service.description}</p>
            {service.details && (
              <div className="simulator-modal-details">
                <p>{service.details}</p>
              </div>
            )}
            {service.price && (
              <div className="simulator-modal-price">
                <Sparkles className="simulator-price-icon" />
                <strong>Option premium : +{service.price}€</strong>
              </div>
            )}
            {service.included && (
              <div className="simulator-modal-included">
                <Check className="simulator-check-icon" />
                <strong>Inclus dans votre offre !</strong>
              </div>
            )}
            {service.autoconsumption && (
              <div className="simulator-modal-included">
                <TrendingUp className="simulator-check-icon" />
                <strong>Autoconsommation : {service.autoconsumption}%</strong>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  const BillModal = ({ onClose }) => (
    <div className="simulator-modal-overlay" onClick={onClose}>
      <div className="simulator-modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="simulator-modal-header">
          <div className="simulator-modal-icon">
            <Euro />
          </div>
          <h3>Affiner ma facture électrique</h3>
          <button onClick={onClose} className="simulator-modal-close">
            <X size={20} />
          </button>
        </div>
        <div className="simulator-modal-body">
          <p>Ajustez les paramètres de votre facture pour un calcul plus précis</p>
          
          <div className="simulator-modal-details">
            <div className="simulator-input-group">
              <label>Facture {viewMode === 'monthly' ? 'mensuelle' : 'annuelle'}</label>
              <div className="simulator-bill-input-container">
                <input
                  type="number"
                  value={viewMode === 'monthly' ? formData.monthlyBill : formData.monthlyBill * 12}
                  onChange={(e) => {
                    const value = parseFloat(e.target.value) || 0;
                    const monthlyValue = viewMode === 'monthly' ? value : value / 12;
                    setFormData({ ...formData, monthlyBill: Math.round(monthlyValue / 10) * 10 });
                  }}
                  className="simulator-bill-input"
                  style={{ fontSize: '1.25rem' }}
                  step={viewMode === 'monthly' ? '10' : '120'}
                  min={viewMode === 'monthly' ? '50' : '600'}
                  max={viewMode === 'monthly' ? '400' : '4800'}
                />
                <span className="simulator-bill-currency">€</span>
              </div>
              <input
                type="range"
                min={viewMode === 'monthly' ? '50' : '600'}
                max={viewMode === 'monthly' ? '400' : '4800'}
                step={viewMode === 'monthly' ? '10' : '120'}
                value={viewMode === 'monthly' ? formData.monthlyBill : formData.monthlyBill * 12}
                onChange={(e) => {
                  const value = parseInt(e.target.value);
                  const monthlyValue = viewMode === 'monthly' ? value : value / 12;
                  setFormData({ ...formData, monthlyBill: monthlyValue });
                }}
                className="simulator-range-slider"
              />
              <div className="simulator-range-labels">
                <span>{viewMode === 'monthly' ? '50€' : '600€'}</span>
                <span>{viewMode === 'monthly' ? '400€' : '4800€'}</span>
              </div>
            </div>

            <div className="simulator-input-group" style={{ marginTop: '1.5rem' }}>
              <label>Prix du kWh</label>
              <div className="simulator-bill-input-container">
                <input
                  type="number"
                  value={formData.electricityPrice}
                  onChange={(e) => setFormData({ ...formData, electricityPrice: parseFloat(e.target.value) || 0.2276 })}
                  className="simulator-bill-input"
                  style={{ fontSize: '1.25rem' }}
                  step="0.001"
                  min="0.15"
                  max="0.30"
                />
                <span className="simulator-bill-currency">€</span>
              </div>
              <input
                type="range"
                min="0.15"
                max="0.30"
                step="0.001"
                value={formData.electricityPrice}
                onChange={(e) => setFormData({ ...formData, electricityPrice: parseFloat(e.target.value) })}
                className="simulator-range-slider"
              />
              <div className="simulator-range-labels">
                <span>0.15€</span>
                <span>0.30€</span>
              </div>
            </div>

            <div className="simulator-consumption-stats" style={{ marginTop: '1.5rem' }}>
              <div className="simulator-stat-item">
                <span>Consommation {viewMode === 'monthly' ? 'mensuelle' : 'annuelle'}</span>
                <strong>
                  {viewMode === 'monthly' 
                    ? `${Math.round(getEstimatedYearlyConsumption() / 12)} kWh`
                    : `${Math.round(getEstimatedYearlyConsumption())} kWh`
                  }
                </strong>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const renderStep1 = () => (
    <div className="simulator-step-container simulator-step-1">
      <div className="simulator-hero-section">
        <div className="simulator-hero-content">
          <div className="simulator-hero-badge">
            <Star className="simulator-badge-icon" />
            <span>Solutions solaires premium</span>
          </div>
          <h1 className="simulator-hero-title">
            Transformez votre toit en 
            <span className="simulator-highlight-text"> centrale électrique</span>
          </h1>
          <p className="simulator-hero-subtitle">
            <strong>RÉDUISEZ VOTRE FACTURE ÉLECTRIQUE JUSQU'À 90%</strong>
            <span className="simulator-subtitle-features">
              <span className="simulator-feature-pill">LOA dès 65€/mois</span>
              <span className="simulator-feature-pill">Installation premium</span>
              <span className="simulator-feature-pill">Garantie 25 ans</span>
            </span>
          </p>
          
          {/* CONTENEUR PRINCIPAL AVEC POSITION RELATIVE */}
          <div className="simulator-main-form-container">
            
            {/* SECTION ADRESSE */}
            <div className="simulator-address-section">
              <div className="simulator-search-instruction">
                <MapPin className="simulator-instruction-icon" />
                <span>Découvrez votre potentiel solaire en 2 minutes</span>
              </div>
              
              {/* CONTENEUR RECHERCHE AVEC POSITION RELATIVE */}
              <div className="simulator-address-search-container" ref={searchContainerRef}>
                <div className="simulator-address-input-wrapper">
                  <MapPin className="simulator-search-icon" />
                  <input
                    ref={searchInputRef}
                    type="text"
                    placeholder="Entrez votre adresse (ex: 123 rue de la Paix, Paris)"
                    className="simulator-address-input"
                    value={formData.address}
                    onChange={handleAddressInputChange}
                    onFocus={() => {
                      console.log('🎯 Focus sur input, suggestions disponibles:', addressSuggestions.length);
                      if (addressSuggestions.length > 0) {
                        console.log('👁️ Affichage des suggestions existantes');
                        setShowSuggestions(true);
                      }
                    }}
                  />
                  {isSearching && (
                    <div className="simulator-loading-indicator">
                      <Loader size={16} className="simulator-spinning" />
                    </div>
                  )}
                </div>
                
                {/* LISTE DES SUGGESTIONS - POSITION ABSOLUE PAR RAPPORT AU CONTENEUR */}
                {showSuggestions && addressSuggestions.length > 0 && (
                  <div ref={suggestionsRef} className="simulator-address-suggestions">
                    {addressSuggestions.map((suggestion, index) => (
                      <div
                        key={`${suggestion.properties.id || index}-${suggestion.properties.label}`}
                        onClick={() => handleAddressSelect(suggestion)}
                        className="simulator-suggestion-item"
                      >
                        <div className="simulator-suggestion-content">
                          <div className="simulator-suggestion-label">
                            {suggestion.properties.label}
                          </div>
                          {suggestion.properties.context && (
                            <div className="simulator-suggestion-context">
                              {suggestion.properties.context}
                            </div>
                          )}
                        </div>
                        <div className="simulator-suggestion-arrow">
                          →
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
            
            {/* SECTION FACTURE ÉLECTRIQUE */}
            <div className="simulator-bill-section">
              <div className="simulator-search-instruction">
                <Euro className="simulator-instruction-icon" />
                <span>Indiquez le montant de votre facture électrique mensuelle</span>
              </div>
              
              <div className="simulator-bill-input-container">
                <input
                  type="number"
                  value={formData.monthlyBill || ''}
                  onChange={(e) => setFormData({ ...formData, monthlyBill: parseInt(e.target.value) || 0 })}
                  className="simulator-bill-input"
                  step="10"
                  min="50"
                  max="500"
                  placeholder="150"
                />
                <span className="simulator-bill-currency">€/mois</span>
              </div>
              
              <input
                type="range"
                min="50"
                max="500"
                step="10"
                value={formData.monthlyBill || 150}
                onChange={(e) => setFormData({ ...formData, monthlyBill: parseInt(e.target.value) })}
                className="simulator-range-slider"
              />
              
              <div className="simulator-range-labels">
                <span>50€</span>
                <span>500€</span>
              </div>
              
              <div className="simulator-consumption-stats">
                <div className="simulator-stat-item">
                  <span>Consommation annuelle estimée</span>
                  <strong>
                    {formData.monthlyBill ? `${Math.round((formData.monthlyBill * 12) / formData.electricityPrice)} kWh` : '0 kWh'}
                  </strong>
                </div>
              </div>
            </div>
            
            {/* BOUTON CTA */}
            <div className="simulator-cta-section">
              <button 
                className="simulator-main-cta-button"
                onClick={nextStep}
                disabled={!formData.coordinates || !formData.monthlyBill || formData.monthlyBill < 50}
                type="button"
              >
                <span className="simulator-btn-text">Calculer mes économies</span>
                <span className="simulator-btn-icon">⚡</span>
              </button>
            </div>
            
          </div>
          
          {/* MESSAGE DE SUCCÈS */}
          {formData.coordinates && (
            <div className="simulator-success-message">
              <div className="simulator-success-icon-wrapper">
                <Check className="simulator-success-icon" />
              </div>
              <div className="simulator-success-content">
                <span className="simulator-success-title">Excellent choix !</span>
                <span className="simulator-success-text">Votre toiture est parfaitement exposée</span>
              </div>
            </div>
          )}

          {/* INDICATEURS DE CONFIANCE */}
          <div className="simulator-trust-indicators">
            <div className="simulator-trust-item">
              <Star className="simulator-trust-icon" />
              <span>4.9/5 - 2000+ clients satisfaits</span>
            </div>
            <div className="simulator-trust-item">
              <a 
                href="https://www.acfluide.fr/wp-content/uploads/2025/05/Ac-Fluide-QualiPV.pdf" 
                target="_blank" 
                rel="noopener noreferrer"
                className="simulator-trust-link"
              >
                <img 
                  src="https://www.qualit-enr.org/wp-content/uploads/2024/12/logo-QualiPV-2025-RGE_sc.png" 
                  alt="RGE QualiPV Certifié" 
                  className="simulator-rge-logo"
                />
                <span>Certifié RGE QualiPV</span>
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const renderStep2 = () => (
    <div className="simulator-step-container simulator-step-2">
      <div className="simulator-content-layout">
        <div className="simulator-left-panel">
          <div className="simulator-section-header">
            <h2 className="simulator-section-title">
              Configurez votre installation
            </h2>
            <p className="simulator-section-subtitle">
              Optimisez votre installation pour maximiser vos économies
            </p>
          </div>
          
          {/* Panel types */}
          <div className="simulator-config-section">
            <Tooltip id="kwc">
              <h3>Choisissez votre gamme de panneaux</h3>
            </Tooltip>
            <div className="simulator-panel-types-grid">
              {Object.entries(panelTypes).map(([key, type]) => (
                <div
                  key={key}
                  className={`simulator-panel-type-card ${formData.panelType === key ? 'simulator-selected' : ''}`}
                  onClick={() => {
                    setFormData({ ...formData, panelType: key });
                    if (typeof gtag !== 'undefined') {
                      gtag('event', 'panel_type_selected', {
                        event_category: 'configuration',
                        event_label: key,
                        panel_type: type.name,
                        utm_source: tracking.utm_source || 'direct'
                      });
                    }
                  }}
                >
                  <div className="simulator-panel-recommendation">
                    {type.recommendation}
                  </div>
                  <div className="simulator-panel-badge">
                    <span>{type.badge}</span>
                  </div>
                  <div className="simulator-panel-type-name">{type.name}</div>
                  <div className="simulator-panel-type-description">{type.description}</div>
                  <div className="simulator-panel-specs">
                    <div className="simulator-spec-item">
                      <span>Efficacité</span>
                      <strong>{type.efficiencyLabel}</strong>
                    </div>
                    <div className="simulator-spec-item">
                      <span>Garantie</span>
                      <strong>{type.warranty}</strong>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Panel count */}
          <div className="simulator-config-section">
            <h3>Puissance de votre installation</h3>
            <div className="simulator-panel-count-options">
              {[6, 9, 12, 15, 18, 21].map((count) => (
                <div
                  key={count}
                  className={`simulator-panel-count-option ${formData.panelCount === count ? 'simulator-selected' : ''}`}
                  onClick={() => {
                    setFormData({ ...formData, panelCount: count });
                  }}
                >
                  <div className="simulator-panel-count-number">{count} panneaux</div>
                  <div className="simulator-panel-count-power">{count * 0.5} kWc</div>
                  <div className="simulator-panel-count-production">
                    {solarProduction ? 
                      `~${Math.round(solarProduction * (count / formData.panelCount))} kWh/an` :
                      `~${Math.round(count * 0.5 * panelTypes[formData.panelType].efficiency / 0.22 * 1250)} kWh/an`
                    }
                  </div>
                  {count === 12 && <div className="simulator-popular-badge">Populaire</div>}
                </div>
              ))}
            </div>
          </div>

          {/* Battery types with advantages/disadvantages */}
          <div className="simulator-config-section">
            <Tooltip id="autoconsommation">
              <h3>Optimisez votre autoconsommation</h3>
            </Tooltip>
            <p className="simulator-battery-subtitle">Choisissez comment utiliser au mieux votre production solaire</p>
            
            <div className="simulator-battery-types-grid">
              {Object.entries(batteryTypes).map(([key, type]) => {
                const IconComponent = type.icon;
                return (
                  <div
                    key={key}
                    className={`simulator-battery-type-card ${formData.batteryType === key ? 'simulator-selected' : ''} ${type.isRecommended ? 'simulator-recommended' : ''}`}
                    onClick={() => {
                      setFormData({ ...formData, batteryType: key });
                    }}
                  >
                    {type.isRecommended && (
                      <div className="simulator-recommendation-badge">
                        <Star size={14} />
                        <span>RECOMMANDÉ</span>
                      </div>
                    )}
                    
                    <div className="simulator-battery-header">
                      <div className="simulator-battery-icon">
                        <IconComponent />
                      </div>
                      <div className="simulator-battery-info">
                        <h4>{type.name}</h4>
                        <p>{type.description}</p>
                      </div>
                    </div>

                    <div className="simulator-battery-stats">
                      <div className="simulator-battery-stat">
                        <span>Autoconsommation</span>
                        <strong className="simulator-autoconso-rate">{formData.batteryType === key ? getAutoconsumptionRate() : type.autoconsumption}%</strong>
                      </div>
                      <div className="simulator-battery-stat">
                        <span>Coût mensuel</span>
                        <strong>
                          {key === 'none' && '0€'}
                          {key === 'physical' && `${Math.round((type.costPerKwh * formData.batteryCapacity) / (formData.leasingDuration * 12))}€`}
                          {key === 'virtual' && `${formData.panelCount * 0.5 * type.costPerKwc}€`}
                        </strong>
                      </div>
                    </div>

                    <div className="simulator-battery-details">
                      <div className="simulator-advantages">
                        <h5><CheckCircle size={16} /> Avantages</h5>
                        <ul>
                          {type.advantages.map((advantage, index) => (
                            <li key={index}>{advantage}</li>
                          ))}
                        </ul>
                      </div>
                      
                      {type.disadvantages.length > 0 && (
                        <div className="simulator-disadvantages">
                          <h5><XCircle size={16} /> Points d'attention</h5>
                          <ul>
                            {type.disadvantages.map((disadvantage, index) => (
                              <li key={index}>{disadvantage}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>

                    <button 
                      className="simulator-battery-learn-more"
                      onClick={(e) => {
                        e.stopPropagation();
                        setShowModal({
                          name: type.name,
                          description: type.detailedDescription,
                          icon: IconComponent,
                          advantages: type.advantages,
                          disadvantages: type.disadvantages,
                          autoconsumption: type.autoconsumption,
                          ...(key === 'virtual' && { isRecommended: true })
                        });
                      }}
                      type="button"
                    >
                      <Info size={14} />
                      En savoir plus
                    </button>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Physical battery configuration */}
          {formData.batteryType === 'physical' && (
            <div className="simulator-battery-config">
              <div className="simulator-power-header">
                <label>Capacité de stockage</label>
                <span className="simulator-power-value">{formData.batteryCapacity} kWh</span>
              </div>
              <input
                type="range"
                min="5"
                max="20"
                step="2.5"
                value={formData.batteryCapacity}
                onChange={(e) => setFormData({ ...formData, batteryCapacity: parseFloat(e.target.value) })}
                className="simulator-range-slider"
              />
              <div className="simulator-range-labels">
                <span>5 kWh</span>
                <span>20 kWh</span>
              </div>
              <div className="simulator-battery-benefit">
                Autoconsommation estimée : {getAutoconsumptionRate()}%
              </div>
            </div>
          )}

          {/* Leasing section */}
          <div className="simulator-config-section">
            <Tooltip id="loa">
              <h3>Durée de votre LOA</h3>
            </Tooltip>
            <p className="simulator-leasing-subtitle">Choisissez la durée qui vous convient le mieux</p>
            
            <div className="simulator-leasing-grid">
              {Object.entries(leasingOptions).map(([duration, option]) => (
                <div
                  key={duration}
                  className={`simulator-leasing-card ${formData.leasingDuration == duration ? 'simulator-selected' : ''} ${option.popular ? 'simulator-popular' : ''}`}
                  onClick={() => setFormData({ ...formData, leasingDuration: parseInt(duration) })}
                >
                  {option.popular && <div className="simulator-popular-badge">Recommandé</div>}
                  
                  <div className="simulator-leasing-duration">
                    <span className="simulator-years">{option.years}</span>
                    <span className="simulator-years-label">ans</span>
                  </div>
                  
                  <div className="simulator-leasing-details">
                    <div className="simulator-monthly-amount">
                      <span className="simulator-amount-value">
                        {getEstimatedMonthlyPayment(parseInt(duration))}€
                      </span>
                      <span className="simulator-amount-label">/mois</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Car charger option */}
          <div className="simulator-config-section">
            <h3>Option borne de recharge</h3>
            
            <div className={`simulator-option-card ${formData.withCarCharger ? 'simulator-active' : ''}`}>
              <div className="simulator-option-header">
                <div className="simulator-option-info">
                  <Zap className="simulator-option-icon" />
                  <div>
                    <h4>Borne de recharge électrique 7kW</h4>
                    <p>Rechargez votre véhicule avec l'énergie solaire</p>
                    <button 
                      className="simulator-info-link"
                      onClick={() => setShowModal(services.carCharger)}
                      type="button"
                    >
                      En savoir plus
                    </button>
                  </div>
                </div>
                <label className="simulator-toggle">
                  <input
                    type="checkbox"
                    checked={formData.withCarCharger}
                    onChange={(e) => setFormData({ ...formData, withCarCharger: e.target.checked })}
                  />
                  <span className="simulator-toggle-slider"></span>
                </label>
              </div>
              
              {formData.withCarCharger && (
                <div className="simulator-option-price">
                  <div className="simulator-price-details">
                    <span>Installation incluse</span>
                    <strong>+{services.carCharger.price}€</strong>
                  </div>
                  <div className="simulator-charging-benefit">
                    Rechargez 100% gratuit avec vos panneaux !
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Services inclus */}
          <div className="simulator-services-section">
            <h3>Tout inclus dans votre offre AC Fluide</h3>
            
            <div className="simulator-services-grid">
              <div className="simulator-service-card">
                <div className="simulator-service-icon">
                  <User />
                </div>
                <div className="simulator-service-content">
                  <h4>Réalisation de l'installation</h4>
                  <p>Une équipe de techniciens certifiés RGE réalise votre installation solaire chez vous, à la date convenue en amont. Il faut généralement un à deux jours pour poser les panneaux solaires sur votre toiture et les brancher à votre tableau électrique.</p>
                  <div className="simulator-service-included">
                    <Check className="simulator-check-icon" />
                    <span>La réalisation de votre installation solaire est incluse dans votre offre.</span>
                  </div>
                </div>
              </div>

              <div className="simulator-service-card">
                <div className="simulator-service-icon">
                  <Award />
                </div>
                <div className="simulator-service-content">
                  <Tooltip id="garantie">
                    <h4>Garantie AC Fluide</h4>
                  </Tooltip>
                  <p>En choisissant AC Fluide pour votre projet solaire, vous bénéficiez de notre garantie pièces et main d'œuvre (en plus de la garantie constructeur) pendant :</p>
                  <ul>
                    <li>20 ans en location (10 pour la batterie)</li>
                    <li>10 ans en achat comptant</li>
                  </ul>
                  <div className="simulator-service-included">
                    <Check className="simulator-check-icon" />
                    <span>Garantie et maintenance incluses.</span>
                  </div>
                </div>
              </div>

              <div className="simulator-service-card">
                <div className="simulator-service-icon">
                  <Euro />
                </div>
                <div className="simulator-service-content">
                  <Tooltip id="vente_surplus">
                    <h4>Vendez l'électricité que vous ne consommez pas</h4>
                  </Tooltip>
                  <p>Lorsque vous produisez plus d'électricité que vous n'en consommez, EDF O.A. ou votre régie locale achète automatiquement votre surplus. Vous bénéficiez ainsi d'un revenu supplémentaire.</p>
                  <div className="simulator-service-benefit">
                    <TrendingUp className="simulator-benefit-icon" />
                    <span>Tarif de rachat : 0,04€/kWh</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Location info */}
          <div className="simulator-info-section">
            <div className="simulator-info-card">
              <AlertCircle className="simulator-info-icon" />
              <div className="simulator-info-content">
                <h4>Location</h4>
                <p>La location est une souscription mensuelle qui ne nécessite pas d'investissement initial. AC Fluide se charge de la maintenance de votre installation pendant la durée. Vous pouvez racheter l'installation quand vous le souhaitez à partir de la 10e année.</p>
              </div>
            </div>
          </div>

          {/* CTA */}
          <div className="simulator-cta-section">
            <div className="simulator-cta-card">
              <div className="simulator-cta-content">
                <h3>Votre installation est configurée !</h3>
                <p>Obtenez votre devis personnalisé maintenant</p>
                <div className="simulator-cta-benefits">
                  <div className="simulator-cta-benefit">
                    <Check className="simulator-cta-check" />
                    <span>Économies : {calculateSavings()}€/mois</span>
                  </div>
                  <div className="simulator-cta-benefit">
                    <Check className="simulator-cta-check" />
                    <span>LOA : {getEstimatedMonthlyPayment()}€/mois</span>
                  </div>
                  <div className="simulator-cta-benefit">
                    <Check className="simulator-cta-check" />
                    <span>Devis gratuit et sans engagement</span>
                  </div>
                </div>
                <button 
                  onClick={nextStep} 
                  className="simulator-main-cta-btn"
                  type="button"
                >
                  <span>Obtenir mon devis personnalisé</span>
                  <ChevronRight className="simulator-cta-arrow" />
                </button>
              </div>
            </div>
          </div>
        </div>

        <div className="simulator-right-panel">
          {/* View mode switch */}
          <div className="simulator-view-mode-switch">
            <div className="simulator-switch-container">
              <span className={`simulator-switch-label ${viewMode === 'monthly' ? 'active' : ''}`}>
                Vue mensuelle
              </span>
              <label className="simulator-toggle-switch">
                <input
                  type="checkbox"
                  checked={viewMode === 'yearly'}
                  onChange={(e) => setViewMode(e.target.checked ? 'yearly' : 'monthly')}
                />
                <span className="simulator-switch-slider"></span>
              </label>
              <span className={`simulator-switch-label ${viewMode === 'yearly' ? 'active' : ''}`}>
                Vue annuelle
              </span>
            </div>
          </div>

          <div className="simulator-price-card">
            <div className="simulator-price-header">
              <Sparkles className="simulator-sparkle-icon" />
              <span>Votre LOA sur-mesure</span>
            </div>
            
            {pvgisLoading ? (
              <div className="simulator-pvgis-loading-enhanced">
                <div className="simulator-pvgis-animation">
                  <div className="simulator-calculation-steps">
                    {[
                      { name: 'Géolocalisation', icon: MapPin, status: pvgisStep >= 0 ? 'completed' : 'pending' },
                      { name: 'Données météo', icon: Cloud, status: pvgisStep >= 1 ? 'completed' : pvgisStep === 0 ? 'active' : 'pending' },
                      { name: 'Calcul production', icon: Cpu, status: pvgisStep >= 2 ? 'completed' : pvgisStep === 1 ? 'active' : 'pending' },
                      { name: 'Optimisation', icon: TrendingUp, status: pvgisStep >= 3 ? 'completed' : pvgisStep === 2 ? 'active' : 'pending' },
                      { name: 'Économies', icon: Euro, status: pvgisStep >= 4 ? 'completed' : pvgisStep === 3 ? 'active' : 'pending' }
                    ].map((stepItem, index) => (
                      <div key={index} className={`simulator-calculation-step simulator-${stepItem.status}`}>
                        <div className="simulator-step-icon">
                          {stepItem.status === 'completed' && <Check size={16} />}
                          {stepItem.status === 'active' && <Loader size={16} className="simulator-spinning" />}
                          {stepItem.status === 'pending' && <stepItem.icon size={16} />}
                        </div>
                        <span>{stepItem.name}</span>
                      </div>
                    ))}
                  </div>
                  <div className="simulator-calculation-description">
                    <h4>Calcul en cours...</h4>
                    <Tooltip id="pvgis">
                      <p>Analyse PVGIS de votre potentiel solaire</p>
                    </Tooltip>
                  </div>
                </div>
              </div>
            ) : (
              <>
                <div className="simulator-new-bill-highlight">
                  <div className="simulator-highlight-label">
                    Votre nouvelle facture {viewMode === 'monthly' ? 'mensuelle' : 'annuelle'}*
                  </div>
                  <div className="simulator-new-bill-amount">
                    {viewMode === 'monthly' 
                      ? `${calculateNewBill()}€`
                      : `${calculateNewBill() * 12}€`
                    }
                  </div>
                  <div className="simulator-old-bill-crossed">
                    Avant : <span>
                      {viewMode === 'monthly' 
                        ? `${formData.monthlyBill}€`
                        : `${formData.monthlyBill * 12}€`
                      }
                    </span>
                  </div>
                  <div className="simulator-economy-badge">
                    <ArrowDown className="simulator-economy-icon" />
                    -{viewMode === 'monthly' 
                      ? `${calculateSavings()}€`
                      : `${calculateSavings() * 12}€`
                    } d'économies !
                  </div>
                  <div className="simulator-bill-disclaimer">
                    * Hors coûts d'acheminement et abonnement
                  </div>
                </div>

                <div className="simulator-price-breakdown">
                  <div className="simulator-price-item">
                    <span>LOA {viewMode === 'monthly' ? 'mensuelle' : 'annuelle'}</span>
                    <strong>
                      {viewMode === 'monthly' 
                        ? `${getEstimatedMonthlyPayment()}€`
                        : `${getEstimatedMonthlyPayment() * 12}€`
                      }
                    </strong>
                  </div>
                  <div className="simulator-price-item">
                    <span>Économies {viewMode === 'monthly' ? 'mensuelles' : 'annuelles'}</span>
                    <strong className="simulator-savings-text">
                      -{viewMode === 'monthly' 
                        ? `${calculateSavings()}€`
                        : `${calculateSavings() * 12}€`
                      }
                    </strong>
                  </div>
                  {formData.batteryType === 'virtual' && (
                    <div className="simulator-price-item">
                      <span>Batterie virtuelle {viewMode === 'monthly' ? '(mensuel)' : '(annuel)'}</span>
                      <strong>
                        +{viewMode === 'monthly' 
                          ? `${formData.panelCount * 0.5 * batteryTypes.virtual.costPerKwc}€`
                          : `${formData.panelCount * 0.5 * batteryTypes.virtual.costPerKwc * 12}€`
                        }
                      </strong>
                    </div>
                  )}
                  <div className="simulator-price-total">
                    <span>Impact net sur votre budget</span>
                    <strong className={`simulator-net-impact ${getEstimatedMonthlyPayment() - calculateSavings() <= 0 ? 'positive' : 'negative'}`}>
                      {getEstimatedMonthlyPayment() - calculateSavings() > 0 ? '+' : ''}
                      {viewMode === 'monthly' 
                        ? `${getEstimatedMonthlyPayment() - calculateSavings()}€`
                        : `${(getEstimatedMonthlyPayment() - calculateSavings()) * 12}€`
                      }
                    </strong>
                  </div>
                </div>
                
                <div className="simulator-leasing-selected">
                  <Clock className="simulator-clock-icon" />
                  <span>LOA sur {formData.leasingDuration} ans</span>
                </div>
                
                <div className="simulator-benefits-list">
                  <div className="simulator-benefit-item">
                    <Check className="simulator-check-icon" />
                    <span>0€ d'apport initial</span>
                  </div>
                  <div className="simulator-benefit-item">
                    <Check className="simulator-check-icon" />
                    <span>Installation incluse</span>
                  </div>
                  <div className="simulator-benefit-item">
                    <Check className="simulator-check-icon" />
                    <span>Maintenance incluse</span>
                  </div>
                  <div className="simulator-benefit-item">
                    <Check className="simulator-check-icon" />
                    <span>Autoconsommation : {getAutoconsumptionRate()}%</span>
                  </div>
                </div>

                {/* Bouton affiner facture */}
                <div style={{ margin: '1.5rem 0' }}>
                  <button 
                    onClick={() => setShowBillModal(true)}
                    className="simulator-service-details-btn"
                    style={{ width: '100%', justifyContent: 'center' }}
                    type="button"
                  >
                    <Euro size={14} />
                    Affiner ma facture ({formData.monthlyBill}€/mois)
                  </button>
                </div>

                {/* Économies sur 25 ans */}
                {savings && (
                  <div className="simulator-long-term-savings">
                    <Tooltip id="economies_25_ans">
                      <h4>Économies sur 25 ans</h4>
                    </Tooltip>
                    <div className="simulator-savings-value">
                      {savings.total.toLocaleString()}€
                    </div>
                    <p>Tenant compte de l'inflation du prix de l'électricité (+3% par an)</p>
                  </div>
                )}

                <div className="simulator-production-highlight">
                  <div className="simulator-production-card">
                    <div className="simulator-production-icon">
                      <Zap />
                    </div>
                    <div className="simulator-production-details">
                      <h4 className="simulator-production-title">Production annuelle</h4>
                      <div className="simulator-production-value">
                        {solarProduction ? (
                          `${Math.round(solarProduction)} kWh`
                        ) : (
                          `Calcul...`
                        )}
                      </div>
                      <p className="simulator-production-subtitle">
                        {solarProduction ? (
                          <>Autoconsommation : {getAutoconsumptionRate()}% • 
                          Efficacité {panelTypes[formData.panelType].efficiencyLabel}</>
                        ) : (
                          "Analyse PVGIS en cours..."
                        )}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="simulator-right-panel-cta">
                  <button 
                    onClick={nextStep} 
                    className="simulator-devis-cta-btn"
                    type="button"
                  >
                    <span>Mon devis gratuit</span>
                    <ChevronRight />
                  </button>
                  <p className="simulator-cta-disclaimer">Sans engagement • Réponse sous 24h</p>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );

  const renderStep3 = () => (
    <div className="simulator-step-container simulator-step-3">
      <div className="simulator-contact-hero">
        <div className="simulator-contact-icon">
          <Sparkles />
        </div>
        <h2 className="simulator-contact-title">Votre simulation est prête !</h2>
        <p className="simulator-contact-subtitle">
          Recevez votre devis personnalisé et planifiez un rendez-vous
        </p>
        
        <div className="simulator-savings-preview-big">
          <div className="simulator-preview-item">
            <span>Économies annuelles</span>
            <strong className="simulator-positive">{Math.round(calculateSavings() * 12)}€</strong>
          </div>
          <div className="simulator-preview-item">
            <span>Nouvelle facture mensuelle*</span>
            <strong className="simulator-positive">{calculateNewBill()}€</strong>
          </div>
          <div className="simulator-preview-item">
            <span>LOA mensuelle</span>
            <strong>{getEstimatedMonthlyPayment()}€</strong>
          </div>
        </div>
      </div>
      
      <div className="simulator-contact-form">
        <div className="simulator-form-grid">
          <div className="simulator-form-group">
            <label className="simulator-form-label">Votre prénom *</label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className="simulator-form-input"
              placeholder="Jean"
            />
          </div>
          
          <div className="simulator-form-group">
            <label className="simulator-form-label">Votre nom *</label>
            <input
              type="text"
              value={formData.lastName}
              onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
              className="simulator-form-input"
              placeholder="Dupont"
            />
          </div>
        </div>
        
        <div className="simulator-form-group">
          <label className="simulator-form-label">Email *</label>
          <div className="simulator-input-with-icon">
            <Mail className="simulator-input-icon" />
            <input
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              className="simulator-form-input simulator-input-with-icon-field"
              placeholder="jean.dupont@gmail.com"
            />
          </div>
        </div>
        
        <div className="simulator-form-group">
          <label className="simulator-form-label">Téléphone *</label>
          <div className="simulator-input-with-prefix">
            <span className="simulator-phone-prefix">FR +33</span>
            <input
              type="tel"
              value={formData.phone}
              onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
              className="simulator-form-input simulator-phone-input"
              placeholder="06 12 34 56 78"
            />
          </div>
        </div>
        
        <div className="simulator-consent-box">
          <label className="simulator-consent-label">
            <input
              type="checkbox"
              checked={formData.acceptMarketing}
              onChange={(e) => setFormData({ ...formData, acceptMarketing: e.target.checked })}
              className="simulator-consent-checkbox"
            />
            <span>
              J'autorise AC Fluide à me recontacter pour finaliser mon projet solaire. *
            </span>
          </label>
        </div>

        <div className="simulator-timeline">
          <h4 className="simulator-timeline-title">Votre projet solaire en 4 étapes</h4>
          
          <div className="simulator-timeline-steps">
            {[
              { title: "Simulation personnalisée", time: "2 minutes", icon: Calculator, status: "done" },
              { title: "Rendez-vous expert gratuit", time: "24h", icon: User, status: "next" },
              { title: "Validation administrative", time: "2-4 semaines", icon: Award, status: "future" },
              { title: "Installation & mise en service", time: "1-2 jours", icon: Home, status: "future" }
            ].map((item, index) => (
              <div key={index} className={`simulator-timeline-step simulator-${item.status}`}>
                <div className="simulator-timeline-icon">
                  <item.icon />
                  {item.status === 'done' && <Check className="simulator-status-check" />}
                </div>
                <div className="simulator-timeline-content">
                  <p className="simulator-timeline-step-title">{item.title}</p>
                  <p className="simulator-timeline-step-time">{item.time}</p>
                </div>
              </div>
            ))}
          </div>

          <div className="simulator-contact-support">
            <div className="simulator-support-header">
              <Phone className="simulator-support-icon" />
              <h5>Nos conseillers sont disponibles</h5>
            </div>
            <div className="simulator-support-contacts">
              <div className="simulator-support-item">
                <Phone className="simulator-support-item-icon" />
                <div>
                  <span className="simulator-support-label">Appelez-nous</span>
                  <a href="tel:+33465844645" className="simulator-support-value simulator-phone-link">+33 4 65 84 46 45</a>
                </div>
              </div>
              <div className="simulator-support-item">
                <Mail className="simulator-support-item-icon" />
                <div>
                  <span className="simulator-support-label">Écrivez-nous</span>
                  <a href="mailto:contact@acfluide.fr" className="simulator-support-value simulator-email-link">contact@acfluide.fr</a>
                </div>
              </div>
            </div>
            <p className="simulator-support-hours">Du lundi au vendredi, 9h-18h</p>
          </div>
        </div>
      </div>
    </div>
  );

  const renderStep4 = () => (
    <div className="simulator-step-container simulator-step-4">
      <div className="simulator-offer-hero">
        <div className="simulator-offer-icon">
          <Star />
        </div>
        <h2 className="simulator-offer-title">Votre projet solaire personnalisé</h2>
        <p className="simulator-offer-subtitle">Simulation complète pour {formData.address}</p>
      </div>
      
      {loading ? (
        <div className="simulator-loading">
          <div className="simulator-loading-spinner"></div>
          <p>Calcul de votre potentiel solaire en cours...</p>
        </div>
      ) : solarProduction && monthlyPayment && savings ? (
        <div className="simulator-offer-content">
          <div className="simulator-left-panel">
            <div className="simulator-main-savings-highlight">
              <h3>Vos économies exceptionnelles</h3>
              <div className="simulator-savings-hero">
                <div className="simulator-savings-main">
                  <span>Nouvelle facture mensuelle*</span>
                  <div className="simulator-new-bill-mega">{Math.round(savings.newMonthlyBill)}€</div>
                  <div className="simulator-old-bill-mega">au lieu de {formData.monthlyBill}€</div>
                  <div className="simulator-bill-disclaimer-main">* Hors coûts d'acheminement et abonnement</div>
                </div>
                <div className="simulator-savings-details">
                  <div className="simulator-saving-detail">
                    <span>Économies par mois</span>
                    <strong className="simulator-positive">{savings.monthly}€</strong>
                  </div>
                  <div className="simulator-saving-detail">
                    <span>Économies par an</span>
                    <strong className="simulator-positive">{savings.yearly}€</strong>
                  </div>
                  <div className="simulator-saving-detail">
                    <span>Autoconsommation</span>
                    <strong>{savings.autoconsumptionRate}%</strong>
                  </div>
                </div>
              </div>
            </div>

            <div className="simulator-installation-summary">
              <h3 className="simulator-section-title">
                Votre installation premium
              </h3>
              
              <div className="simulator-summary-grid">
                <div className="simulator-summary-item">
                  <Zap className="simulator-summary-icon" />
                  <div>
                    <h4>{formData.panelCount} × Panneaux {panelTypes[formData.panelType].name}</h4>
                    <p>Production : {Math.round(solarProduction)} kWh/an</p>
                    <p>Puissance : {formData.panelCount * 0.5} kWc</p>
                  </div>
                </div>
                
                <div className="simulator-summary-item">
                  {React.createElement(batteryTypes[formData.batteryType].icon, { className: "simulator-summary-icon" })}
                  <div>
                    <h4>{batteryTypes[formData.batteryType].name}</h4>
                    <p>Autoconsommation : {savings.autoconsumptionRate}%</p>
                    {formData.batteryType === 'physical' && <p>Capacité : {formData.batteryCapacity} kWh</p>}
                    {formData.batteryType === 'virtual' && <p>Coût : {formData.panelCount * 0.5}€/mois</p>}
                  </div>
                </div>
                
                {formData.withCarCharger && (
                  <div className="simulator-summary-item">
                    <Zap className="simulator-summary-icon" />
                    <div>
                      <h4>Borne de recharge 7kW</h4>
                      <p>Recharge 100% verte</p>
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className="simulator-services-included">
              <div className="simulator-services-header">
                <h3 className="simulator-section-title">
                  Tout inclus dans votre offre
                </h3>
              </div>
              
              <div className="simulator-services-grid-enhanced">
                {Object.entries(services).filter(([key, service]) => service.included).map(([key, service]) => (
                  <div key={key} className="simulator-service-item-enhanced">
                    <div className="simulator-service-header">
                      <div className="simulator-service-icon-wrapper">
                        <service.icon className="simulator-service-icon" />
                      </div>
                      <div className="simulator-included-badge-new">
                        <Check className="simulator-check-icon-small" />
                        <span>INCLUS</span>
                      </div>
                    </div>
                    <div className="simulator-service-content">
                      <h4>{service.name}</h4>
                      <p className="simulator-service-description">{service.description}</p>
                      <button 
                        className="simulator-service-details-btn"
                        onClick={() => setShowModal(service)}
                        type="button"
                      >
                        <Info size={14} />
                        Voir détails
                      </button>
                    </div>
                  </div>
                ))}
              </div>

              <div className="simulator-premium-guarantee">
                <div className="simulator-guarantee-icon">
                  <Award className="simulator-guarantee-icon-svg" />
                </div>
                <div className="simulator-guarantee-content">
                  <h4>Garantie Premium AC Fluide</h4>
                  <div className="simulator-guarantee-features">
                    <span>25 ans produit</span>
                    <span>20 ans installation</span>
                    <span>Assurance décennale</span>
                    <span>SAV prioritaire</span>
                  </div>
                  <div className="simulator-rge-certification">
                    <Tooltip id="rge">
                      <a 
                        href="https://www.acfluide.fr/wp-content/uploads/2025/05/Ac-Fluide-QualiPV.pdf" 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="simulator-rge-badge"
                      >
                        <img 
                          src="https://www.qualit-enr.org/wp-content/uploads/2024/12/logo-QualiPV-2025-RGE_sc.png" 
                          alt="Certificat RGE QualiPV" 
                          className="simulator-rge-cert-logo"
                        />
                        <span>Voir certificat RGE</span>
                      </a>
                    </Tooltip>
                  </div>
                </div>
              </div>
            </div>

            <div className="simulator-environmental-impact">
              <Leaf className="simulator-environmental-icon" />
              <div>
                <h3>Votre impact environnemental</h3>
                <p>Vous éviterez <strong>{Math.round(solarProduction * 0.000459)} tonnes de CO2</strong> par an</p>
                <p>Équivalent à planter <strong>{Math.round(solarProduction * 0.000459 * 16)} arbres</strong> !</p>
              </div>
            </div>

            <div className="simulator-final-cta-container">
              <p className="simulator-cta-subtitle">Votre demande a été enregistrée avec succès !</p>
              <p className="simulator-cta-subtitle">Un expert AC Fluide vous contactera dans les 24h.</p>
            </div>
          </div>
          
          <div className="simulator-right-panel">
            <div className="simulator-pricing-summary">
              <div className="simulator-summary-header">
                <Star className="simulator-summary-star" />
                <h3>Récapitulatif de votre offre</h3>
              </div>
              
              <div className="simulator-bill-transformation">
                <div className="simulator-transformation-title">Transformation de votre facture*</div>
                <div className="simulator-bill-before-after">
                  <div className="simulator-bill-old">
                    <span>Aujourd'hui</span>
                    <div className="simulator-amount-old">{formData.monthlyBill}€</div>
                  </div>
                  <div className="simulator-transformation-arrow">
                    <ArrowDown className="simulator-arrow" />
                  </div>
                  <div className="simulator-bill-new">
                    <span>Avec AC Fluide</span>
                    <div className="simulator-amount-new">{Math.round(savings.newMonthlyBill)}€</div>
                  </div>
                </div>
                <div className="simulator-monthly-savings">
                  {savings.monthly}€ d'économies par mois !
                </div>
                <div className="simulator-bill-disclaimer-summary">
                  * Hors coûts d'acheminement et abonnement
                </div>
              </div>
              
              <div className="simulator-pricing-details">
                <div className="simulator-detail-section">
                  <h4>Votre installation</h4>
                  <div className="simulator-detail-item">
                    <span>{formData.panelCount} panneaux {panelTypes[formData.panelType].name}</span>
                    <span>{formData.panelCount * 0.5} kWc</span>
                  </div>
                  <div className="simulator-detail-item">
                    <span>{batteryTypes[formData.batteryType].name}</span>
                    <span>{savings.autoconsumptionRate}% autoconso.</span>
                  </div>
                  <div className="simulator-detail-item">
                    <span>Production PVGIS</span>
                    <span>{Math.round(solarProduction)} kWh/an</span>
                  </div>
                  <div className="simulator-detail-item">
                    <span>Consommation estimée</span>
                    <span>{savings.estimatedConsumption} kWh/an</span>
                  </div>
                  {formData.withCarCharger && (
                    <div className="simulator-detail-item">
                      <span>Borne de recharge</span>
                      <span>7 kW</span>
                    </div>
                  )}
                </div>
                
                <div className="simulator-detail-section">
                  <h4>Votre LOA</h4>
                  <div className="simulator-detail-item">
                    <span>Durée</span>
                    <span>{formData.leasingDuration} ans</span>
                  </div>
                  <div className="simulator-detail-item simulator-prominent">
                    <span>Mensualité LOA</span>
                    <span>{monthlyPayment}€</span>
                  </div>
                  {formData.batteryType === 'virtual' && (
                    <div className="simulator-detail-item">
                      <span>Dont batterie virtuelle</span>
                      <span>{formData.panelCount * 0.5}€</span>
                    </div>
                  )}
                </div>
                
                <div className="simulator-detail-section">
                  <h4>Vos économies</h4>
                  <div className="simulator-detail-item">
                    <span>Par mois</span>
                    <span className="simulator-savings-amount">{savings.monthly}€</span>
                  </div>
                  <div className="simulator-detail-item">
                    <span>Par an</span>
                    <span className="simulator-savings-amount">{savings.yearly}€</span>
                  </div>
                  <div className="simulator-detail-item">
                    <span>Sur 25 ans</span>
                    <span className="simulator-savings-amount">{savings.total.toLocaleString()}€</span>
                  </div>
                </div>
              </div>
              
              <div className="simulator-guarantee-badges">
                <div className="simulator-badge-item">
                  <Shield className="simulator-badge-icon" />
                  <span>Garantie 25 ans</span>
                </div>
                <div className="simulator-badge-item">
                  <TrendingUp className="simulator-badge-icon" />
                  <span>{savings.autoconsumptionRate}% autoconso.</span>
                </div>
                <div className="simulator-badge-item">
                  <Check className="simulator-badge-icon" />
                  <span>0€ d'apport</span>
                </div>
              </div>
              
              <div className="simulator-contact-expert">
                <div className="simulator-expert-header">
                  <Phone className="simulator-expert-icon" />
                  <div>
                    <p>Une question ? Nos experts</p>
                    <span className="simulator-expert-subtitle">Conseiller dédié disponible</span>
                  </div>
                </div>
                <div className="simulator-expert-contacts">
                  <div className="simulator-expert-contact">
                    <Phone className="simulator-phone-icon" />
                    <div className="simulator-contact-details">
                      <span className="simulator-contact-type">Téléphone</span>
                      <a href="tel:+33465844645" className="simulator-phone-link">
                        <strong>+33 4 65 84 46 45</strong>
                      </a>
                    </div>
                  </div>
                  <div className="simulator-expert-contact">
                    <Mail className="simulator-phone-icon" />
                    <div className="simulator-contact-details">
                      <span className="simulator-contact-type">Email</span>
                      <a href="mailto:contact@acfluide.fr" className="simulator-email-link">
                        <strong>contact@acfluide.fr</strong>
                      </a>
                    </div>
                  </div>
                </div>
                <div className="simulator-expert-hours">
                  <Clock className="simulator-hours-icon" />
                  <span>Lun-Ven 9h-18h</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );

  const renderCurrentStep = () => {
    switch (step) {
      case 1: return renderStep1();
      case 2: return renderStep2();
      case 3: return renderStep3();
      case 4: return renderStep4();
      default: return null;
    }
  };

  return (
    <div className="simulator-solar-app">
      {/* Header */}
      <header className="simulator-app-header">
        <div className="simulator-header-content">
          <div className="simulator-logo" onClick={handleLogoClick} style={{ cursor: 'pointer' }}>
            <img 
              src="/assets/images/ac-fluide-logo.png" 
              alt="AC Fluide Logo" 
              className="simulator-logo-image"
            />
            <div className="simulator-logo-info">
              <span className="simulator-logo-text">AC Fluide</span>
              <span className="simulator-logo-tagline">Solutions Solaires Premium</span>
            </div>
          </div>
          
          <div className="simulator-header-contact">
            <div className="simulator-contact-info">
              <div className="simulator-contact-item">
                <Phone className="simulator-header-contact-icon" />
                <a href="tel:+33465844645" className="simulator-phone-link">+33 4 65 84 46 45</a>
              </div>
              <div className="simulator-contact-item">
                <Mail className="simulator-header-contact-icon" />
                <a href="mailto:contact@acfluide.fr" className="simulator-email-link">contact@acfluide.fr</a>
              </div>
            </div>
            
            {step > 1 && (
              <button className="simulator-expert-btn simulator-desktop-only" onClick={handlePhoneCall} type="button">
                <Phone className="simulator-btn-icon" />
                <span>Conseiller</span>
              </button>
            )}
          </div>
        </div>
      </header>
      
      {/* Progress bar pour étapes > 1 */}
      {step > 1 && (
        <div className="simulator-progress-bar">
          <div className="simulator-progress-content">
            <div className="simulator-progress-navigation">
              <div className="simulator-progress-steps">
                {[
                  { number: 1, label: 'Adresse', icon: MapPin },
                  { number: 2, label: 'Configuration', icon: Sun },
                  { number: 3, label: 'Contact', icon: User },
                  { number: 4, label: 'Offre', icon: Star }
                ].map((stepItem, i) => (
                  <div key={i} className="simulator-progress-step-container">
                    <button 
                      className={`simulator-progress-step ${stepItem.number <= step ? 'simulator-active' : ''} ${stepItem.number < step ? 'simulator-completed' : ''}`}
                      onClick={() => stepItem.number < step && setStep(stepItem.number)}
                      disabled={stepItem.number > step}
                      type="button"
                    >
                      {stepItem.number < step ? (
                        <Check size={14} />
                      ) : (
                        <stepItem.icon size={14} />
                      )}
                    </button>
                    {i < 3 && <div className={`simulator-progress-line ${stepItem.number < step ? 'simulator-completed' : stepItem.number === step ? 'simulator-active' : ''}`} />}
                  </div>
                ))}
              </div>
              <div className="simulator-progress-labels">
                <span className={step >= 1 ? 'simulator-active' : ''}>Adresse</span>
                <span className={step >= 2 ? 'simulator-active' : ''}>Config</span>
                <span className={step >= 3 ? 'simulator-active' : ''}>Contact</span>
                <span className={step >= 4 ? 'simulator-active' : ''}>Offre</span>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Main content */}
      <main className="simulator-app-main">
        {renderCurrentStep()}
      </main>
      
      {/* Sticky footer pour navigation */}
      {step > 1 && step < 4 && (
        <div className="simulator-sticky-footer">
          <div className="simulator-sticky-content">
            {step > 1 && (
              <div className="simulator-sticky-nav-left">
                <button onClick={prevStep} className="simulator-nav-btn simulator-prev" type="button">
                  <ChevronLeft />
                  Retour
                </button>
              </div>
            )}
            
            <div className="simulator-sticky-savings">
              <div className="simulator-sticky-title">Votre facture électrique</div>
              <div className="simulator-sticky-bills-comparison">
                <div className="simulator-sticky-bill-old">
                  <span>Avant</span>
                  <div className="simulator-sticky-amount-old">{formData.monthlyBill}€</div>
                </div>
                <div className="simulator-sticky-arrow">→</div>
                <div className="simulator-sticky-bill-new">
                  <span>Après</span>
                  <div className="simulator-sticky-amount-new">
                    {pvgisLoading ? '...' : calculateNewBill()}€
                  </div>
                </div>
              </div>
            </div>
            
            <div className="simulator-sticky-pricing">
              <div className="simulator-sticky-price">
                <span className="simulator-sticky-label">LOA mensuelle</span>
                <span className="simulator-sticky-amount">{getEstimatedMonthlyPayment()}€</span>
              </div>
              
              <button 
                onClick={nextStep} 
                className="simulator-sticky-cta" 
                type="button"
                disabled={isSubmitting && step === 3}
              >
                {step === 3 ? (isSubmitting ? 'Envoi...' : 'Mon devis') : "Mes économies"}
                <ChevronRight />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modales */}
      {showModal && (
        <Modal 
          service={showModal} 
          onClose={() => setShowModal(null)} 
        />
      )}

      {showBillModal && (
        <BillModal 
          onClose={() => setShowBillModal(false)} 
        />
      )}

      {/* Click outside to close tooltips */}
      {showTooltip && (
        <div 
          className="simulator-tooltip-overlay" 
          onClick={() => setShowTooltip(null)}
        />
      )}

      {/* Footer */}
      <footer className="simulator-app-footer">
        <div className="simulator-footer-content">
          <div className="simulator-footer-main">
            <div className="simulator-footer-company">
              <div className="simulator-footer-logo">
                <img 
                  src="/assets/images/ac-fluide-logo.png" 
                  alt="AC Fluide Logo" 
                  className="simulator-footer-logo-image"
                />
                <div>
                  <span className="simulator-footer-logo-text">AC Fluide</span>
                  <span className="simulator-footer-tagline">Solutions Solaires Premium</span>
                </div>
              </div>
              <p className="simulator-footer-description">
                Spécialiste de l'installation solaire en région PACA. 
                Plus de 2000 clients nous font confiance.
              </p>
            </div>

            <div className="simulator-footer-contact">
              <h4>Contact</h4>
              <div className="simulator-footer-contact-items">
                <div className="simulator-footer-contact-item">
                  <Phone className="simulator-footer-icon" />
                  <div>
                    <span className="simulator-contact-label">Téléphone</span>
                    <a href="tel:+33465844645" className="simulator-contact-value simulator-phone-link">+33 4 65 84 46 45</a>
                  </div>
                </div>
                <div className="simulator-footer-contact-item">
                  <Mail className="simulator-footer-icon" />
                  <div>
                    <span className="simulator-contact-label">Email</span>
                    <a href="mailto:contact@acfluide.fr" className="simulator-contact-value simulator-email-link">contact@acfluide.fr</a>
                  </div>
                </div>
                <div className="simulator-footer-contact-item">
                  <MapPin className="simulator-footer-icon" />
                  <div>
                    <span className="simulator-contact-label">Adresse</span>
                    <span className="simulator-contact-value">474 Route de la grave<br />84210 ALTHEN DES PALUDS</span>
                  </div>
                </div>
              </div>
            </div>

            <div className="simulator-footer-certifications">
              <h4>Certifications</h4>
              <div className="simulator-cert-badges">
                <a 
                  href="https://www.acfluide.fr/wp-content/uploads/2025/05/Ac-Fluide-QualiPV.pdf" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="simulator-cert-badge simulator-cert-link"
                >
                  <img 
                    src="https://www.qualit-enr.org/wp-content/uploads/2024/12/logo-QualiPV-2025-RGE_sc.png" 
                    alt="RGE QualiPV" 
                    className="simulator-cert-rge-logo"
                  />
                  <span>RGE QualiPV</span>
                </a>
                <div className="simulator-cert-badge">
                  <Shield className="simulator-cert-icon" />
                  <span>Assurance décennale</span>
                </div>
                <div className="simulator-cert-badge">
                  <Star className="simulator-cert-icon" />
                  <span>4.9/5 clients</span>
                </div>
              </div>
              <a 
                href="https://www.acfluide.fr/" 
                target="_blank" 
                rel="noopener noreferrer"
                className="simulator-website-link"
              >
                www.acfluide.fr
              </a>
            </div>
          </div>

          <div className="simulator-footer-bottom">
            <p>© 2025 AC Fluide - Tous droits réservés</p>
            <div className="simulator-footer-links">
              <a href="https://www.acfluide.fr/mentions-legales/" target="_blank" rel="noopener noreferrer">
                Mentions légales
              </a>
              <span>•</span>
              <span>CGV</span>
              <span>•</span>
              <a href="https://www.acfluide.fr/politique-de-confidentialite/" target="_blank" rel="noopener noreferrer">
                Confidentialité
              </a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default SolarSimulator;